/**
 * @voltxampere/spice — Recursive-descent parser
 *
 * Sprint 16. The parser turns logical lines from the lexer into a
 * `NetlistDoc` of cards. SPICE is overwhelmingly first-token-typed:
 * a leading dot starts a directive, a leading letter starts an
 * element, anything else (after we strip the title line) is an
 * error or a comment.
 *
 * Element parsing is intentionally loose — we collect nodes,
 * value, and trailing args without knowing what kind of element
 * we're looking at. Codecs (Paket B) take it from there.
 *
 * Error recovery: a malformed card is skipped (recorded as an
 * issue) and parsing continues. Catastrophic failures (e.g. lexer
 * threw) bubble up as a thrown `SpiceParseError`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { lex } from "./lexer.js";
import { SpiceParseError, makeIssue } from "./errors.js";
/**
 * Parse a SPICE3 netlist source. The first non-comment, non-blank
 * line is taken as the title (Berkeley convention). Cards before
 * `.end` (case-insensitive) become the doc body; everything after
 * `.end` is collected as trailing comments.
 */
export function parse(source) {
    let lines;
    try {
        lines = lex(source);
    }
    catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        throw new SpiceParseError("Lexer failed: " + message, []);
    }
    if (lines.length === 0) {
        return {
            ok: true,
            doc: {
                title: "",
                cards: [],
                trailingComments: [],
            },
            warnings: [],
        };
    }
    const issues = [];
    const warnings = [];
    const cards = [];
    const trailingComments = [];
    // SPICE convention: first line is the title. We honour that even
    // if the first line is a comment (they are explicitly the same
    // thing in many netlists — a `* title` line).
    const firstLine = lines[0];
    let title = "";
    let cursor = 0;
    if (firstLine !== undefined) {
        title = extractTitleText(firstLine);
        cursor = 1;
    }
    let endSeen = false;
    while (cursor < lines.length) {
        const ll = lines[cursor];
        cursor += 1;
        if (ll === undefined)
            continue;
        if (endSeen) {
            // Anything past `.end` is just trailing commentary.
            const text = collectText(ll);
            if (text.length > 0)
                trailingComments.push(text);
            continue;
        }
        if (ll.isComment) {
            const first = ll.tokens[0];
            if (first === undefined)
                continue;
            cards.push({
                kind: "comment",
                text: first.value,
                line: ll.line,
            });
            continue;
        }
        if (ll.tokens.length === 0)
            continue;
        const head = ll.tokens[0];
        if (head === undefined)
            continue;
        // `.<directive>` line.
        if (head.type === "WORD" && head.value.startsWith(".")) {
            const directive = head.value.toLowerCase();
            if (directive === ".end") {
                endSeen = true;
                continue;
            }
            const args = collectArgs(ll.tokens.slice(1));
            const card = {
                kind: "directive",
                directive,
                args,
                line: ll.line,
                col: head.col,
            };
            cards.push(card);
            // Directives we don't know about generate warnings, not
            // hard errors.
            if (!isKnownDirective(directive)) {
                warnings.push(makeIssue("spice/unknown-directive", `Unknown directive '${directive}' (Sprint 16 ignores it)`, { line: ll.line, col: head.col }));
            }
            continue;
        }
        // Otherwise expect an element card. SPICE elements are keyed
        // by the FIRST CHARACTER of the identifier — `R1` is a
        // resistor, `Q3` is a BJT, etc.
        if (head.type !== "WORD") {
            issues.push(makeIssue("spice/element-needs-identifier", `Expected element identifier, got ${head.type}`, { line: ll.line, col: head.col }));
            continue;
        }
        const identifier = head.value;
        const letter = identifier.charAt(0).toUpperCase();
        if (!/^[A-Z]/.test(letter)) {
            issues.push(makeIssue("spice/element-letter-required", `Element identifier must start with a letter, got '${identifier}'`, { line: ll.line, col: head.col }));
            continue;
        }
        const card = parseElementCard(letter, identifier, ll.tokens.slice(1), ll.line, head.col);
        cards.push(card);
    }
    // Hard error wins — if we collected issues, return failure with a
    // best-effort partial doc.
    if (issues.length > 0) {
        return {
            ok: false,
            issues,
            partial: { title, cards, trailingComments },
        };
    }
    return {
        ok: true,
        doc: { title, cards, trailingComments },
        warnings,
    };
}
function extractTitleText(line) {
    if (line.isComment) {
        const t = line.tokens[0];
        return t ? t.value : "";
    }
    // Non-comment first line — title is the raw text.
    return line.rawText.trim();
}
function collectText(line) {
    if (line.isComment) {
        const t = line.tokens[0];
        return t ? t.value : "";
    }
    return line.rawText.trim();
}
function collectArgs(tokens) {
    const args = [];
    for (const t of tokens) {
        if (t.type === "COMMENT")
            break;
        if (t.type === "EQ" || t.type === "COMMA")
            continue;
        args.push(t.value);
    }
    return args;
}
/**
 * Element card layout: nodes... value [extra args...].
 *
 * Strategy: collect every WORD/NUMBER/PAREN/STRING token. The
 * codec layer pulls nodes off the front (it knows how many it
 * needs), takes the next as the value, and treats the rest as
 * extra args. We do NOT attempt to be clever here — codecs handle
 * the parens-bracketed `PULSE(...)` body on their own.
 */
function parseElementCard(letter, identifier, tail, line, col) {
    // Collect non-comment tokens; preserve parens/comma intact so
    // codecs can locate the `PULSE(...)` block.
    const allTokens = [];
    for (const t of tail) {
        if (t.type === "COMMENT")
            break;
        allTokens.push(t);
    }
    // Heuristic: the codec expects N node names followed by one
    // value/model and optional extras. We don't know N here, so we
    // expose every WORD/NUMBER token as a flat string list. The
    // codec slices them.
    const flat = [];
    for (let i = 0; i < allTokens.length; i++) {
        const t = allTokens[i];
        if (t === undefined)
            continue;
        if (t.type === "EQ" || t.type === "COMMA")
            continue;
        if (t.type === "LPAREN") {
            // Collect until matching RPAREN — this is the function-body
            // syntax (`PULSE(...)` / `SIN(...)` / `EXP(...)`).
            const fn = flat[flat.length - 1] ?? "";
            let body = `${fn}(`;
            flat.pop();
            let depth = 1;
            i += 1;
            while (i < allTokens.length && depth > 0) {
                const inner = allTokens[i];
                if (inner === undefined)
                    break;
                if (inner.type === "LPAREN")
                    depth += 1;
                else if (inner.type === "RPAREN") {
                    depth -= 1;
                    if (depth === 0) {
                        body += ")";
                        break;
                    }
                }
                if (inner.type === "COMMA") {
                    body += " ";
                }
                else {
                    body += (body.endsWith("(") ? "" : " ") + inner.value;
                }
                i += 1;
            }
            flat.push(body);
            continue;
        }
        flat.push(t.value);
    }
    // Codec slicing convention:
    //   const nodes = card.tokens.slice(0, codec.nodeCount)
    //   const value = card.tokens[codec.nodeCount] ?? null
    //   const extras = card.tokens.slice(codec.nodeCount + 1)
    return {
        kind: "element",
        letter,
        identifier,
        tokens: flat,
        line,
        col,
    };
}
const KNOWN_DIRECTIVES = new Set([
    ".dc",
    ".tran",
    ".ac",
    ".op",
    ".end",
    ".ends",
    ".global",
    ".include",
    ".lib",
    ".model",
    ".options",
    ".param",
    ".print",
    ".plot",
    ".save",
    ".subckt",
    ".step",
    ".measure",
    ".meas",
    ".ic",
    ".nodeset",
    ".tf",
    ".sens",
    ".disto",
    ".four",
    ".noise",
]);
function isKnownDirective(directive) {
    return KNOWN_DIRECTIVES.has(directive);
}
//# sourceMappingURL=parser.js.map